/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  MSNAdmainCIDRouterMap,
  MSNAIDRouterMap,
  MSNCIDRouterMap,
  useAIDRouterConfig,
  useCIDRouterConfig,
  useCrossBorderAIDRouterMapConfig,
  useCrossBorderCIDRouterMapConfig,
  useSettlementManagementCIDRouterMapConfig,
  useSettlementManagementAIDRouterMapConfig
} from "@/routes/routerMap";
import { classNamesFunction, KeyCodes, ThemeProvider } from "@fluentui/react";
import type { INavLinkGroup } from "@m365-admin/nav";
import { Nav } from "@m365-admin/nav";
import { useState, useRef, useEffect } from "react";
import { IResponsiveNavStyleProps, IResponsiveNavStyles, M365NavMainStyle, getResponsiveNavStyles } from "./menuStyles";
import { M365ActualLightTheme, useM365Theme } from "@m365-admin/customizations";
import { usePrevious } from "@fluentui/react-hooks";
import { selectLinkAddClick } from "@/utilities/menuUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { ParseRoute } from "./getNavLink";
import { getCurrentInfo, getParameter } from "@/utilities/common";
import { CustomerAgencyType } from "@/utilities/common_enum";
import { verifyProduct } from "@/utilities/function";
import { useStore } from "@/utilities/storeHook";
import dayjs from "@/utilities/dayjs";

const getClassNames = classNamesFunction<
  IResponsiveNavStyleProps,
  IResponsiveNavStyles
>();

const collapseButtonID = "collapseButton";
const hamburgerButtonID = "hamburgerButton";

const NavExample = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { commonStore } = useStore();
  const { pathname } = location;
  const CID = getParameter("cid");
  const AID = getParameter("aid");
  const [selectedKey, setSelectedKey] = useState(pathname);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [isNavInOverlay, setIsNavInOverlay] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState<boolean>();
  const [reduceMargins, setReduceMargins] = useState(false);
  const [showOverlayNav, setShowOverlayNav] = useState(false);
  const theme = useM365Theme();
  const preventSoftDismiss = useRef<boolean>();
  const rafID = useRef<number>();
  const rootRef = useRef<HTMLDivElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const cidRoutes = useCIDRouterConfig();
  const aidRoutes = useAIDRouterConfig();
  // 使用国际化的CrossBorder路由配置
  const crossBorderCIDRoutes = useCrossBorderCIDRouterMapConfig();
  const crossBorderAIDRoutes = useCrossBorderAIDRouterMapConfig();
  // 使用国际化的SettlementManagement路由配置
  const settlementManagementCIDRoutes = useSettlementManagementCIDRouterMapConfig();
  const settlementManagementAIDRoutes = useSettlementManagementAIDRouterMapConfig();
  let menuLinkGroups: any = ParseRoute(cidRoutes);
  const [navLinkGroups, setNavLinkGroups] = useState<INavLinkGroup[]>([]);
  useEffect(() => {
    if (getCurrentInfo("userRoles") === undefined) {
      navigate("/notfound");
      return;
    }
    const productType = Number(getCurrentInfo("productType"));
    const primaryPurpose = Number(getCurrentInfo("primaryPurpose"));
    const roleId = Number(getCurrentInfo("userRoles")[0]);
    const roleIds = [1, 3];
    const isCN = getCurrentInfo("region").toLowerCase() === "zh-cn";
    dayjs.tz.setDefault(isCN ? "Asia/Shanghai" : "Asia/tokyo");
    if (verifyProduct(productType, primaryPurpose)) {
      if (AID) {
        menuLinkGroups = ParseRoute(MSNAIDRouterMap);
      } else {
        menuLinkGroups = ParseRoute(roleIds?.includes(roleId) ? MSNAdmainCIDRouterMap : MSNCIDRouterMap);
      }
    } else {
      const customerType = getCurrentInfo("customerType");
      if (customerType === CustomerAgencyType.BillingAgency) {
        if (AID) {
          menuLinkGroups = ParseRoute(settlementManagementAIDRoutes);
        } else {
          menuLinkGroups = ParseRoute(settlementManagementCIDRoutes);
        }
      } else {
        if (AID) {
          menuLinkGroups = isCN ? ParseRoute(aidRoutes) : ParseRoute(crossBorderAIDRoutes);
        } else {
          menuLinkGroups = isCN ? ParseRoute(cidRoutes) : ParseRoute(crossBorderCIDRoutes);
        }
      }
    }
  }, [AID, CID]);

  useEffect(() => {
    setSelectedKey(pathname);
  }, [pathname]);

  useEffect(() => {
    setNavLinkGroups([{
      key: "Navigation",
      links: [...menuLinkGroups.links],
    }
    ]);
  }, [AID, CID]);
  /**
   * Detect when a click is outside the navigation itself and close the nav if in overlay mode
   */
  const softDismiss = (ev: MouseEvent) => {
    if (
      !preventSoftDismiss.current &&
      showOverlayNav &&
      navContainerRef.current &&
      ev.target instanceof Node &&
      !navContainerRef.current.contains(ev.target)
    ) {
      setShowOverlayNav(false);
    }
    preventSoftDismiss.current = false;
  };

  const escNav = (ev: KeyboardEvent) => {
    // We need to have keyCode here as a fallback for IE11 since code is not implemented there
    // tslint:disable-next-line:deprecation
    const keyCode = ev.code || ev.keyCode;

    if (
      keyCode === KeyCodes.escape ||
      (typeof keyCode === "string" && keyCode.toLowerCase() === "escape")
    ) {
      setShowOverlayNav(false);
    }
  };

  /**
   * Callback that is used in our resize event handler
   */
  const handleResize = () => {
    if (rafID.current) {
      cancelAnimationFrame(rafID.current);
    }
    rafID.current = requestAnimationFrame(updateNavStates);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    // check the size of our app initially so we set the right things.
    updateNavStates();
    window.addEventListener("click", softDismiss);
    window.addEventListener("keydown", escNav);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("click", softDismiss);
      window.removeEventListener("keydown", escNav);
    };
  });

  const previousShowOverlayNav = usePrevious(showOverlayNav);

  useEffect(() => {
    if (!previousShowOverlayNav && showOverlayNav) {
      document.getElementById(collapseButtonID)?.focus();
    }

    if (previousShowOverlayNav && !showOverlayNav) {
      document.getElementById(hamburgerButtonID)?.focus();
    }
  }, [showOverlayNav]);

  /**
   * Function that determines if we need to adjust our state based on the root width
   */
  const updateNavStates = () => {
    // For this example we are listening to window events but actually checking the width of the example.
    // This is to ensure the example functions as if it was the window width. Most apps can likely listen\
    // to innerWidth on window
    if (rootRef.current) {
      const windowWidth = rootRef.current.clientWidth;

      if (windowWidth < 480 && !reduceMargins) {
        setReduceMargins(true);
      } else if (windowWidth > 480 && reduceMargins) {
        setReduceMargins(false);
      }

      // put us in overlay nav mode and reset the nav show prop once we cross the threshold
      if (windowWidth < 640 && !isNavInOverlay) {
        setIsNavInOverlay(true);
        setShowOverlayNav(false);
      } else if (windowWidth > 640 && isNavInOverlay) {
        setIsNavInOverlay(false);
        setShowOverlayNav(false);
      }
    }
  };

  const _onNavCollapsed = (_isNavCollapsed: boolean): void => {
    if (showOverlayNav) {
      setIsNavCollapsed(!isNavCollapsed);
      setShowOverlayNav(!showOverlayNav);
    } else {
      setIsNavCollapsed(!isNavCollapsed);
    }
    commonStore.setMenuCollapsed(!isNavCollapsed);
  };

  const _selectKey = (key: string): void => {
    setSelectedKey(key);
    setShowOverlayNav(false);
    navigate(`${key}?cid=${CID}${AID ? "&aid=" + AID : ""}`);
  };

  const overlayEnabledAndShow = isNavInOverlay && showOverlayNav;
  const classNames = getClassNames(getResponsiveNavStyles, {
    theme,
    reduceMargins,
    isNavInOverlay,
  });

  useEffect(() => {
    const pathnames = location.pathname.split("/").map((key: string) => `/${key}`);
    const key = pathnames[1];
    setExpandedKeys([key]);
  }, []);
  const onExpand = (key: string) => {
    const keys = [...expandedKeys];
    const expand = !keys.includes(key);
    if (expand) {
      keys.push(key);
    } else {
      const index = keys.findIndex((eKey: string) => eKey === key);
      keys.splice(index, 1);
    }
    setExpandedKeys(keys);
  };
  return (
    <ThemeProvider theme={M365ActualLightTheme}>
      <div className={M365NavMainStyle}>
        {(!isNavInOverlay || overlayEnabledAndShow) && (
          <Nav
            groups={selectLinkAddClick(navLinkGroups, selectedKey, _selectKey, onExpand, expandedKeys)}
            isNavCollapsed={isNavInOverlay ? false : isNavCollapsed}
            onNavCollapsed={_onNavCollapsed}
            styles={{
              root: {
                height: "100%",
                backgroundColor: "#fff!important",
                zIndex: 9
              },
              navWrapper: {
                ">div": {
                  ">button": {
                    backgroundColor: "#fff!important"
                  }
                }
              },
              navContainer: {
                backgroundColor: "#fff!important"
              },
            }}
            collapseNavLinkProps={{
              id: collapseButtonID,
              title: isNavCollapsed ? "Expand Navigation" : "Collapse Navigation",
              "aria-label": isNavCollapsed
                ? "Expand Navigation"
                : "Collapse Navigation",
              ...(isNavInOverlay && {
                "aria-expanded": showOverlayNav,
                "aria-checked": !isNavCollapsed,
                role: "button",
                "aria-label": "Hide Navigation",
              }),
            }}
          />
        )}
      </div>
    </ThemeProvider>
  );
};
export default NavExample;
